import ClickAwayListener from "@mui/material/ClickAwayListener";
import Stack from "@mui/material/Stack";
import Tabs from "@mui/material/Tabs";
import { useMainContentFilter } from "core/hooks/useMainContentFilter";
import { useMenuItems } from "core/hooks/useMenuItems";
import { useRouter } from "next/router";
import { memo, useCallback, useEffect, useState } from "react";
import { BrandLogo } from "../BrandLogo";
import { CustomLink } from "../CustomLink";
import { DesktopGlobalHeaderNavTab } from "./DesktopGlobalHeaderNavTab";

function MenuAutoCloseHandler({
  setIsFilterOn,
  setIsMenuOpen,
}: {
  setIsFilterOn: (value: boolean) => void;
  setIsMenuOpen: (value: boolean) => void;
}) {
  const router = useRouter();
  useEffect(() => {
    function closeMenuOnRouteChange() {
      setIsMenuOpen(false);
      setIsFilterOn(false);
    }
    const eventArgs: Parameters<typeof router.events.on> = [
      "routeChangeStart",
      closeMenuOnRouteChange,
    ];
    router.events.on(...eventArgs);
    return () => {
      router.events.off(...eventArgs);
    };
  });
  return null;
}

export const DesktopGlobalHeaderNav = memo(() => {
  const { 1: setIsFilterOn } = useMainContentFilter();
  const { menuItems } = useMenuItems();
  const [tabIndex, setTabIndex] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [transitionProperty, setTransitionProperty] = useState<
    "transform" | "all"
  >("transform");

  const changeTab = useCallback(
    (index: number, canOpen: boolean) => {
      if (!canOpen) {
        return;
      }
      if (tabIndex === index && isMenuOpen) {
        setIsMenuOpen(false);
        setIsFilterOn(false);
        return;
      }
      setTabIndex(index);
      setIsMenuOpen(true);
      setIsFilterOn(true);
    },
    [isMenuOpen, setIsFilterOn, tabIndex]
  );
  const onClickAway = useCallback(() => {
    setIsMenuOpen(false);
    setIsFilterOn(false);
  }, [setIsFilterOn]);

  return (
    <>
      <MenuAutoCloseHandler
        setIsFilterOn={setIsFilterOn}
        setIsMenuOpen={setIsMenuOpen}
      />
      <Stack
        component="nav"
        direction="row"
        alignItems="center"
        px={6}
        spacing={3}
        bgcolor="background.paper"
        color="primary.dark"
        maxWidth="100vw"
        py={1}
      >
        <CustomLink href="/" passHref display="flex">
          <BrandLogo />
        </CustomLink>
        <ClickAwayListener onClickAway={onClickAway}>
          <Tabs
            component="nav"
            value={tabIndex}
            aria-label="navigation tabs"
            variant="scrollable"
            scrollButtons="auto"
            slotProps={{
              indicator: {
                onTransitionEnd: () => {
                  setTransitionProperty(isMenuOpen ? "all" : "transform");
                },
              },
            }}
            sx={{
              flex: 1,
              "& .MuiTabs-indicator": {
                transform: `scale3d(${isMenuOpen ? 1 : 0},1,1)`,
                transformOrigin: "center",
                transitionProperty,
              },
              "& .MuiTabs-flexContainer": {
                gap: 3,
              },
              "& .MuiTab-root": {
                opacity: 1,
              },
            }}
          >
            {menuItems?.map?.((menuItem, index) => {
              const canOpen = Boolean(
                menuItem.libraries.length || menuItem.submenus.length
              );

              return (
                <DesktopGlobalHeaderNavTab
                  canOpen={canOpen}
                  open={canOpen && isMenuOpen && tabIndex === index}
                  key={menuItem.id}
                  navTabMenuItemId={menuItem.id!}
                  submenus={menuItem.submenus}
                  libraries={menuItem.libraries}
                  displayName={menuItem.displayName ?? ""}
                  target={menuItem.target ?? undefined}
                  url={menuItem.url}
                  changeTab={changeTab}
                  index={index}
                />
              );
            })}
          </Tabs>
        </ClickAwayListener>
      </Stack>
    </>
  );
});
DesktopGlobalHeaderNav.displayName = "DesktopGlobalHeaderNav";
